'use client';

import { cn } from '@core/utils';
import * as RadixDropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import SvgCheck from '../../../__generated__/icon/SvgCheck';
import SvgChevrondown from '../../../__generated__/icon/SvgChevrondown';
import { Checkbox } from '../../atom/Checkbox/Checkbox';
import type { InputProps } from '../../atom/Input/Input';
import { Input } from '../../atom/Input/Input';
import type { DropdownMenuType } from './type';

/**
 * * 드롭다운(셀렉트박스) 목록 요소 타입
 */
interface DropdownMenuItem<T extends string | number> {
  label: string;
  value: T;
}

/**
 * * 드롭다운(셀렉트박스) 컴포넌트 타입
 */
interface DropdownMenuProps<
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
> extends Omit<
    InputProps,
    | 'rightContent'
    | 'type'
    | 'leftContent'
    | 'onClear'
    | 'onChange'
    | 'value'
    | 'onFocus'
    | 'onBlur'
    | 'onInvalid'
  > {
  /** 드롭다운 메뉴 아이템 목록 */
  options: DropdownMenuItem<T>[];
  /** 열림/닫힌 상태 외부 제어용 */
  open?: boolean;
  /** 열림/닫힌 상태 외부 제어용 */
  onOpenChange?: (opened: boolean) => void;
  /** 다중 선택 여부 */
  multiple?: M;
  /** 선택 값 변경 핸들러 */
  onChange?: (value: M extends true ? T[] : T) => void;
  /** 선택 값 */
  value?: M extends true ? T[] : T;
  /** 드롭다운 트리거 타입 */
  type?: Type;
  /** 트리거 크기 설정 (type이 'text'인 경우 사용 불가) */
  dimensions?: Type extends 'text' ? never : InputProps['dimensions'];
  /** 드롭다운 메뉴 위치 (top, bottom, left, right) */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * 드롭다운 메뉴 정렬 (start, center, end)
   * TODO: end를 넣었을 경우 사파리 브라우저, 네이버 브라우저에서 정상적으로 동작하지 않음. 'start'처럼 동작함. 이 경우에 contentClassName에 min-width를 import로 넣어줘야 함.
   */
  align?: 'start' | 'center' | 'end';
  /** 드롭다운 컨텐츠 클래스명 */
  contentClassName?: string;
  /** 드롭다운 메뉴 아이템 비활성화 여부 */
  itemDisabled?: (item: DropdownMenuItem<T>) => boolean;
  /** 검색 기능 활성화 여부 (드롭다운 내부에 검색 input 표시) */
  searchable?: boolean;
  /** 검색 placeholder */
  searchPlaceholder?: string;
}

/**
 * * 드롭다운(셀렉트박스) DS 컴포넌트
 * @summary 디자인 시스템의 드롭다운 컴포넌트입니다.
 * @description DS의 label 및 error, validation message 등등에 대한 처리는 InputDecorator 컴포넌트로 감싸서 사용해주세요
 */
export const DropdownMenu = <
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
>({
  options = [],
  value,
  placeholder,
  onChange,
  invalid,
  disabled,
  dimensions,
  open,
  onOpenChange,
  multiple,
  className,
  type = 'basic' as Type,
  contentClassName,
  side = 'bottom',
  align = 'start',
  itemDisabled,
  defaultValue,
  searchable = false,
  searchPlaceholder = '검색',
}: DropdownMenuProps<T, M, Type>) => {
  const [internalValue, setInternalValue] =
    useState<M extends true ? T[] : T>();
  const [internalOpen, setInternalOpen] = useState(open ?? false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const currentValue = value ?? internalValue ?? defaultValue;
  const displayValue = Array.isArray(currentValue)
    ? currentValue
        .map((v) => options.find((opt) => opt.value === v)?.label ?? v)
        .join(', ')
    : (options.find((opt) => opt.value === currentValue)?.label ??
      currentValue ??
      '');

  // 검색어로 필터링된 옵션
  const filteredOptions =
    searchable && searchQuery
      ? options.filter((item) =>
          item.label.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : options;

  const handleClick = (clickedValue: T) => {
    if (multiple) {
      const current = (
        Array.isArray(value)
          ? value
          : Array.isArray(internalValue)
            ? internalValue
            : []
      ) as T[];
      const newValue = current.includes(clickedValue)
        ? current.filter((v) => v !== clickedValue)
        : [...current, clickedValue];

      setInternalValue(newValue as M extends true ? T[] : T);
      onChange?.(newValue as M extends true ? T[] : T);
    } else {
      setInternalValue(clickedValue as M extends true ? T[] : T);
      onChange?.(clickedValue as M extends true ? T[] : T);
    }
  };

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useLayoutEffect(() => {
    const measureWidth = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    };

    measureWidth();

    const resizeObserver = new ResizeObserver(measureWidth);
    if (triggerRef.current) {
      resizeObserver.observe(triggerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [displayValue]);

  const isOpen = open ?? internalOpen;

  return (
    <RadixDropdownMenuPrimitive.Root
      open={isOpen}
      onOpenChange={(opened) => {
        onOpenChange?.(opened);
        setInternalOpen(opened);

        if (!opened) {
          setSearchQuery('');
        }
      }}
    >
      <RadixDropdownMenuPrimitive.Trigger
        asChild
        disabled={disabled}
      >
        <div
          ref={triggerRef}
          className={cn(
            'relative h-fit min-w-130 select-none overflow-hidden outline-none lg:min-w-150',
            { 'lg:w-fit lg:min-w-[unset] w-fit min-w-[unset]': type === 'text' },
            className,
          )}
          onClick={(e) => {
            e.preventDefault();
            if (!disabled) {
              setInternalOpen(!isOpen);
              onOpenChange?.(!isOpen);
            }
          }}
          onPointerDown={(e) => {
            e.preventDefault();
          }}
        >
          {type === 'basic' ? (
            <Input
              rightContent={
                <SvgChevrondown
                  className={cn('size-16 text-Gray-600 duration-300', {
                    'size-20': dimensions === 'H56',
                    'text-Gray-500': disabled,
                    '-rotate-180 text-Gray-600': isOpen,
                  })}
                />
              }
              value={displayValue}
              placeholder={placeholder}
              className={cn({ 'w-full border-1 border-Gray-900': isOpen })}
              invalid={invalid}
              type="button"
              disabled={disabled}
              dimensions={dimensions}
            />
          ) : (
            <div className="flex items-center justify-between gap-4 overflow-hidden py-10 pl-4 pr-2">
              <p
                className={cn(
                  'flex-1 truncate text-left font-400 text-Gray-900 typo-body4',
                  { 'text-Gray-400': disabled },
                )}
              >
                {displayValue || placeholder}
              </p>
              <SvgChevrondown
                className={cn('size-12 text-Gray-900 duration-300', {
                  '-rotate-180 text-Gray-600': isOpen,
                  'text-Gray-600': displayValue,
                  'text-Gray-400': disabled,
                })}
              />
            </div>
          )}
        </div>
      </RadixDropdownMenuPrimitive.Trigger>
      <RadixDropdownMenuPrimitive.Portal>
        <RadixDropdownMenuPrimitive.Content
          className={cn(
            'max-h-260 w-full min-w-130 overflow-y-auto rounded-xsmall bg-Gray-white py-12 shadow-xl hide-scrollbar lg:min-w-150',
            contentClassName,
          )}
          side={side}
          align={align}
          sideOffset={8}
          sticky="always"
          loop={false}
          style={{
            width: triggerWidth,
            minWidth: type === 'text' ? 'fit-content' : undefined,
            zIndex: 20,
          }}
        >
          {searchable && (
            <div
              className="px-12 pb-8"
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
            >
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                dimensions="H40"
                className="w-full"
              />
            </div>
          )}
          {filteredOptions.length === 0 && searchable && searchQuery ? (
            <div className="px-16 py-12 text-center text-Gray-500 typo-body4">
              검색 결과가 없습니다
            </div>
          ) : (
            filteredOptions?.map((item, index) => {
              const isSelected = Array.isArray(currentValue)
                ? currentValue.includes(item.value)
                : currentValue === item.value;
              const isDisabled = itemDisabled?.(item);

              return (
                <RadixDropdownMenuPrimitive.Item
                  key={index}
                  className="w-full outline-none"
                >
                  <button
                    disabled={isDisabled}
                    className={cn(
                      'flex size-full select-none items-center justify-between gap-8 overflow-hidden px-16 py-8 outline-none duration-150 hover:bg-B-50',
                      {
                        'bg-Gray-100 opacity-50 cursor-not-allowed': isDisabled,
                      },
                    )}
                    onClick={(e) => {
                      if (multiple) e.preventDefault();
                      handleClick(item.value);
                    }}
                  >
                    <div className="pointer-events-none flex items-center gap-4">
                      {multiple && <Checkbox checked={isSelected} />}
                      <p className="line-clamp-1 flex-1 text-left font-400 text-Gray-800 typo-body4">
                        {item.label}
                      </p>
                    </div>
                    {!multiple && isSelected && (
                      <SvgCheck className="size-16 text-B-500" />
                    )}
                  </button>
                </RadixDropdownMenuPrimitive.Item>
              );
            })
          )}
        </RadixDropdownMenuPrimitive.Content>
      </RadixDropdownMenuPrimitive.Portal>
    </RadixDropdownMenuPrimitive.Root>
  );
};
